import React, { useRef, useState } from "react";
import { Stack, Typography, Avatar, Button } from "@mui/material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Styles from "./TaskDetails.module.scss";
import {
  addReply,
  deleteComment,
  deleteReply,
  updateComment,
  updateReply,
} from "../../services/taskManagement.service";
import { ToastError, ToastSuccess } from "../../utils/Toaster/Toaster";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const TimeAgo = ({ date }) => {
  const [displayTime, setDisplayTime] = useState(dayjs(date).fromNow());
  const intervalRef = useRef(null);

  const calculateTime = () => {
    const now = dayjs();
    const diffInSeconds = now.diff(dayjs(date), "second");

    if (diffInSeconds < 60) {
      return `${diffInSeconds} second${diffInSeconds !== 1 ? "s" : ""} ago`;
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes !== 1 ? "s" : ""} ago`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours !== 1 ? "s" : ""} ago`;
    }

    return dayjs(date).format("MMM D, YYYY [at] h:mm A"); // For older than 24 hours
  };

  const handleMouseEnter = () => {
    setDisplayTime(calculateTime());
    intervalRef.current = setInterval(() => {
      setDisplayTime(calculateTime());
    }, 1000);
  };

  const handleMouseLeave = () => {
    clearInterval(intervalRef.current);
  };

  React.useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return (
    <Typography
      sx={{ fontSize: "12px", color: "#000000", cursor: "default" }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {displayTime}
    </Typography>
  );
};

const CommentSection = ({ selectedData, GetSeparateTaskId, currentUser }) => {
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState("");
  const [activeReplyIndex, setActiveReplyIndex] = useState({
    commentIndex: null,
    replyIndex: null,
  });
  const [replyingToReply, setReplyingToReply] = useState(null);

  const handleEditClick = (comment) => {
    setEditingId(comment._id);
    setEditContent(comment.commentText);
  };

  const handleEditSubmit = async (commentId) => {
    try {
      const res = await updateComment({
        taskId: selectedData._id,
        assignedTo: currentUser,
        commentId,
        editContent,
      });

      if (res.data) {
        ToastSuccess(res.data.message);
        GetSeparateTaskId(selectedData._id);
        setEditingId(null);
        setEditContent("");
      }
    } catch (error) {
      console.error("Error editing comment:", error);
    }
  };

  const handleDeleteClick = async (commentId) => {
    try {
      const res = await deleteComment({ taskId: selectedData._id, commentId ,assignedTo: currentUser,});
      if (res?.data) {
        ToastSuccess(res.data?.message);
        GetSeparateTaskId(selectedData._id);
      }
    } catch (error) {
      console.error("Error deleting comment:", error);
    }
  };

  const handleReplySubmit = async (commentId, isReplyToReply = false) => {
    try {
      const data = {
        taskId: selectedData._id,
        commentId,
        reply: {
          assignedTo: currentUser,
          commentText: isReplyToReply ? replyContent : replyContent,
          date: new Date(),
        },
      };
      const res = await addReply(data);
      if (res?.data) {
        ToastSuccess(res.data.message);
        GetSeparateTaskId(selectedData._id);
        setReplyingTo(null);
        setReplyingToReply(null);
        setReplyContent("");
      }
    } catch (error) {
      console.error("Error submitting reply:", error);
    }
  };
  const handleReplyReplySubmit = async (
    commentId,
    replyId,
    isReplyToReply = false
  ) => {
    try {
      const data = {
        taskId: selectedData._id,
        commentId,
        replyId,
        subReply: {
          assignedTo: currentUser,
          commentText: isReplyToReply ? replyContent : replyContent,
          date: new Date(),
        },
      };
      const res = await addReply(data);
      if (res?.data) {
        ToastSuccess(res.data.message);
        GetSeparateTaskId(selectedData._id);
        setReplyingTo(null);
        setReplyingToReply(null);
        setReplyContent("");
      }
    } catch (error) {
      console.error("Error submitting reply:", error);
    }
  };

  const handleEditReply = (commentIndex, replyIndex, reply) => {
    setActiveReplyIndex({ commentIndex, replyIndex });
    setEditContent(reply.commentText);
  };

  const handleDeleteReply = async (commentId, replyId) => {
    try {
      const res = await deleteComment({
        taskId: selectedData._id,
        commentId,
        replyId,
      });
      if (res?.data) {
        ToastSuccess(res.data.message);
        GetSeparateTaskId(selectedData._id);
      }
    } catch (error) {
      console.error("Error deleting reply:", error);
    }
  };

  const handleEditReplySubmit = async (commentId, replyId) => {
    try {
      const res = await updateComment({
        taskId: selectedData._id,
        commentId,
        replyId,
        editContent,
      });
      if (res?.data) {
        ToastSuccess(res.data.message);
        GetSeparateTaskId(selectedData._id);
        setActiveReplyIndex({ commentIndex: null, replyIndex: null });
        setEditContent("");
      }
    } catch (error) {
      console.error("Error updating reply:", error);
    }
  };

  return (
    <div>
      {selectedData?.comments
        ?.filter(
          (comment) =>
            comment &&
            Object.keys(comment).length > 0 &&
            comment.commentText &&
            comment.assignedTo &&
            comment.assignedTo.name
        )
        ?.sort((a, b) => new Date(b.date) - new Date(a.date))
        ?.map((comment, commentIndex) => {
          const isEditingComment = editingId === comment._id;
          const isReplyingToComment = replyingTo === comment._id;

          // {selectedData?.comments?.length > 0 &&
          //   selectedData?.comments?.map((comment, commentIndex) => {
          //     const isEditingComment = editingId === comment._id;
          //     const isReplyingToComment = replyingTo === comment._id;

          return (
            <div key={comment._id || commentIndex}>
              {isEditingComment ? (
                // Edit Comment Mode
                <Stack spacing={1} sx={{ mb: 2 }}>
                  <ReactQuill
                    value={editContent}
                    onChange={setEditContent}
                    placeholder="Edit comment..."
                    style={{
                      borderRadius: "8px",
                      height: "100px",
                      marginBottom: "40px",
                    }}
                    modules={{
                      toolbar: [
                        [{ header: "1" }, { header: "2" }, { font: [] }],
                        [{ list: "ordered" }, { list: "bullet" }],
                        ["bold", "italic", "underline"],
                        [{ align: [] }],
                      ],
                    }}
                    className="EditTextInput"
                  />
                  <Stack direction="row" spacing={1}>
                    <Button
                      onClick={() => handleEditSubmit(comment._id)}
                      className={Styles.commentSaveBtn}
                      size="small"
                    >
                      Save
                    </Button>
                    <Button
                      onClick={() => setEditingId(null)}
                      className={Styles.commentCancelBtn}
                      size="small"
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Stack>
              ) : (
                // Comment View Mode
                <Stack
                  sx={{ mb: 1 }}
                  direction={"row"}
                  spacing={1}
                  alignItems={"flex-start"}
                >
                  <Avatar
                    alt={comment?.assignedTo?.name}
                    src={comment?.assignedTo?.file}
                    style={{
                      backgroundColor: "#C7EEEF",
                      color: "#055253",
                      height: "40px",
                      width: "40px",
                      fontSize: "16px",
                      marginTop: "4px",
                    }}
                  >
                    {comment?.assignedTo?.name?.charAt(0).toUpperCase()}
                  </Avatar>

                  <Stack spacing={0.5} sx={{ width: "100%" }}>
                    <Stack direction={"row"} spacing={1} alignItems="center">
                      <Typography
                        sx={{
                          color: "#000000",
                          fontSize: "16px",
                          fontWeight: "bold",
                        }}
                      >
                        {comment?.assignedTo?.name || "Unknown User"}
                      </Typography>
                      <TimeAgo date={comment.date} />
                    </Stack>

                    <Typography sx={{paddingLeft:"10px"}}
                      dangerouslySetInnerHTML={{ __html: comment.commentText }}
                    />

                    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                      {currentUser === comment?.assignedTo?._id ? (
                        <>
                          <button
                            onClick={() => handleEditClick(comment)}
                            style={{
                              background: "none",
                              border: "none",
                              color: "#055253",
                              cursor: "pointer",
                              fontSize: "12px",
                              padding: 0,
                              textDecoration: "underline",
                            }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteClick(comment._id)}
                            style={{
                              background: "none",
                              border: "none",
                              color: "#055253",
                              cursor: "pointer",
                              fontSize: "12px",
                              padding: 0,
                              textDecoration: "underline",
                            }}
                          >
                            Delete
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => setReplyingTo(comment._id)}
                          style={{
                            background: "none",
                            border: "none",
                            color: "#055253",
                            cursor: "pointer",
                            fontSize: "12px",
                            padding: 0,
                            textDecoration: "underline",
                          }}
                        >
                          Reply
                        </button>
                      )}
                    </Stack>

                    {/* Reply Form */}
                    {isReplyingToComment && (
                      <Stack
                        spacing={1}
                        sx={{ mt: 2, pl: 2, borderLeft: "2px solid #ddd" }}
                      >
                        <Typography sx={{ fontSize: "14px", color: "#666" }}>
                          Replying to @{comment.assignedTo?.name}
                        </Typography>
                        <ReactQuill
                          value={replyContent}
                          onChange={setReplyContent}
                          placeholder="Write your reply..."
                          style={{
                            borderRadius: "8px",
                            height: "100px",
                          }}
                          modules={{
                            toolbar: [
                              [{ header: "1" }, { header: "2" }, { font: [] }],
                              [{ list: "ordered" }, { list: "bullet" }],
                              ["bold", "italic", "underline"],
                              [{ align: [] }],
                            ],
                          }}
                        />
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          <Button
                            onClick={() => handleReplySubmit(comment._id)}
                            className={Styles.commentSaveBtn}
                            size="small"
                          >
                            Save
                          </Button>
                          <Button
                            onClick={() => {
                              setReplyingTo(null);
                              setReplyContent("");
                            }}
                            className={Styles.commentCancelBtn}
                            size="small"
                          >
                            Cancel
                          </Button>
                        </Stack>
                      </Stack>
                    )}

                    {/* Replies List */}
                    {comment.reply?.length > 0 && (
                      <Stack
                        sx={{ mt: 2, pl: 2, borderLeft: "2px solid #ddd" }}
                      >
                        {comment.reply
                        ?.sort((a, b) => new Date(b.date) - new Date(a.date))
                        .map((reply, replyIndex) => {
                          const isEditingReply =
                            activeReplyIndex.commentIndex === commentIndex &&
                            activeReplyIndex.replyIndex === replyIndex;
                          const isReplyingToThisReply =
                            replyingToReply === reply._id;

                          return (
                            <div
                              key={reply._id || replyIndex}
                              style={{ marginBottom: "16px" }}
                            >
                              {isEditingReply ? (
                                // Edit Reply Mode
                                <Stack spacing={1}>
                                  <ReactQuill
                                    value={editContent}
                                    onChange={setEditContent}
                                    placeholder="Edit reply..."
                                    style={{
                                      borderRadius: "8px",
                                      height: "100px",
                                      marginBottom: "10px",
                                    }}
                                    modules={{
                                      toolbar: [
                                        [
                                          { header: "1" },
                                          { header: "2" },
                                          { font: [] },
                                        ],
                                        [
                                          { list: "ordered" },
                                          { list: "bullet" },
                                        ],
                                        ["bold", "italic", "underline"],
                                        [{ align: [] }],
                                      ],
                                    }}
                                  />
                                  <Stack direction="row" spacing={1}>
                                    <Button
                                      onClick={() =>
                                        handleEditReplySubmit(
                                          comment._id,
                                          reply._id
                                        )
                                      }
                                      className={Styles.commentSaveBtn}
                                      size="small"
                                      variant="contained"
                                    >
                                      Save
                                    </Button>
                                    <Button
                                      onClick={() =>
                                        setActiveReplyIndex({
                                          commentIndex: null,
                                          replyIndex: null,
                                        })
                                      }
                                      className={Styles.commentCancelBtn}
                                      size="small"
                                      variant="outlined"
                                    >
                                      Cancel
                                    </Button>
                                  </Stack>
                                </Stack>
                              ) : (
                                // Reply View Mode
                                <>
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="flex-start"
                                  >
                                    <Avatar
                                      alt={reply.assignedTo?.name}
                                      src={reply.assignedTo?.file}
                                      style={{
                                        backgroundColor: "#C7EEEF",
                                        color: "#055253",
                                        height: "32px",
                                        width: "32px",
                                        fontSize: "14px",
                                      }}
                                    >
                                      {reply.assignedTo?.name
                                        ?.charAt(0)
                                        .toUpperCase()}
                                    </Avatar>
                                    <Stack spacing={0.5} sx={{ flexGrow: 1 }}>
                                      <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: "14px",
                                            fontWeight: "bold",
                                          }}
                                        >
                                          {reply.assignedTo?.name ||
                                            "Unknown User"}
                                        </Typography>
                                        <TimeAgo date={reply.date} />
                                      </Stack>
                                      <Typography sx={{paddingLeft:"10px"}}
                                        dangerouslySetInnerHTML={{
                                          __html: reply.commentText,
                                        }}
                                      />
                                      <Stack
                                        direction="row"
                                        spacing={1}
                                        sx={{ mt: 0.5 }}
                                      >
                                        {currentUser ===
                                        reply.assignedTo?._id ? (
                                          <>
                                            <button
                                              onClick={() =>
                                                handleEditReply(
                                                  commentIndex,
                                                  replyIndex,
                                                  reply
                                                )
                                              }
                                              style={{
                                                background: "none",
                                                border: "none",
                                                color: "#055253",
                                                cursor: "pointer",
                                                fontSize: "12px",
                                                padding: 0,
                                                textDecoration: "underline",
                                              }}
                                            >
                                              Edit
                                            </button>
                                            <button
                                              onClick={() =>
                                                handleDeleteReply(
                                                  comment._id,
                                                  reply._id
                                                )
                                              }
                                              style={{
                                                background: "none",
                                                border: "none",
                                                color: "#055253",
                                                cursor: "pointer",
                                                fontSize: "12px",
                                                padding: 0,
                                                textDecoration: "underline",
                                              }}
                                            >
                                              Delete
                                            </button>
                                          </>
                                        ) : (
                                          <button
                                            onClick={() =>
                                              setReplyingToReply(reply._id)
                                            }
                                            style={{
                                              background: "none",
                                              border: "none",
                                              color: "#055253",
                                              cursor: "pointer",
                                              fontSize: "12px",
                                              padding: 0,
                                              textDecoration: "underline",
                                            }}
                                          >
                                            Reply
                                          </button>
                                        )}
                                      </Stack>
                                    </Stack>
                                  </Stack>

                                  {/* Reply to Reply Form */}
                                  {isReplyingToThisReply && (
                                    <Stack
                                      spacing={1}
                                      sx={{
                                        mt: 2,
                                        pl: 2,
                                        borderLeft: "2px solid #ddd",
                                      }}
                                    >
                                      <Typography
                                        sx={{ fontSize: "14px", color: "#666" }}
                                      >
                                        Replying to @{reply.assignedTo?.name}
                                      </Typography>
                                      <ReactQuill
                                        value={replyContent}
                                        onChange={setReplyContent}
                                        placeholder="Write your reply..."
                                        style={{
                                          borderRadius: "8px",
                                          height: "100px",
                                        }}
                                        modules={{
                                          toolbar: [
                                            [
                                              { header: "1" },
                                              { header: "2" },
                                              { font: [] },
                                            ],
                                            [
                                              { list: "ordered" },
                                              { list: "bullet" },
                                            ],
                                            ["bold", "italic", "underline"],
                                            [{ align: [] }],
                                          ],
                                        }}
                                      />
                                      <Stack
                                        direction="row"
                                        spacing={1}
                                        sx={{ mt: 1 }}
                                      >
                                        <Button
                                          onClick={() =>
                                            handleReplyReplySubmit(
                                              comment._id,
                                              reply._id,
                                              false
                                            )
                                          }
                                          className={Styles.commentSaveBtn}
                                          size="small"
                                        >
                                          Save
                                        </Button>
                                        <Button
                                          onClick={() => {
                                            setReplyingToReply(null);
                                            setReplyContent("");
                                          }}
                                          className={Styles.commentCancelBtn}
                                          size="small"
                                        >
                                          Cancel
                                        </Button>
                                      </Stack>
                                    </Stack>
                                  )}
                                </>
                              )}
                              {reply.subReply?.length > 0 && (
                                <Stack
                                  sx={{
                                    mt: 2,
                                    pl: 2,
                                    borderLeft: "2px solid #ddd",
                                  }}
                                >
                                  {reply.subReply
                                  ?.sort((a, b) => new Date(b.date) - new Date(a.date))
                                  .map((sub, subIndex) => {
                                    console.log(sub, "SUBRPLY");
                                    const isEditingSubReply =
                                      activeReplyIndex.commentIndex ===
                                        commentIndex &&
                                      activeReplyIndex.replyIndex ===
                                        replyIndex &&
                                      activeReplyIndex.subReplyIndex ===
                                        subIndex;

                                    const isReplyingToThisSubReply =
                                      replyingToReply === sub._id;

                                    return (
                                      <div
                                        key={sub._id || subIndex}
                                        style={{ marginBottom: "16px" }}
                                      >
                                        {isEditingSubReply ? (
                                          // Edit sub-reply mode
                                          <Stack spacing={1}>
                                            <ReactQuill
                                              value={editContent}
                                              onChange={setEditContent}
                                              placeholder="Edit sub-reply..."
                                              style={{
                                                borderRadius: "8px",
                                                height: "100px",
                                                marginBottom: "10px",
                                              }}
                                              modules={{
                                                toolbar: [
                                                  [
                                                    { header: "1" },
                                                    { header: "2" },
                                                    { font: [] },
                                                  ],
                                                  [
                                                    { list: "ordered" },
                                                    { list: "bullet" },
                                                  ],
                                                  [
                                                    "bold",
                                                    "italic",
                                                    "underline",
                                                  ],
                                                  [{ align: [] }],
                                                ],
                                              }}
                                            />
                                            <Stack direction="row" spacing={1}>
                                              <Button
                                                onClick={() =>
                                                  handleEditReplySubmit(
                                                    comment._id,
                                                    reply._id,
                                                    sub._id
                                                  )
                                                }
                                                className={
                                                  Styles.commentSaveBtn
                                                }
                                                size="small"
                                                variant="contained"
                                              >
                                                Save
                                              </Button>
                                              <Button
                                                onClick={() =>
                                                  setActiveReplyIndex({
                                                    commentIndex: null,
                                                    replyIndex: null,
                                                    subReplyIndex: null,
                                                  })
                                                }
                                                className={
                                                  Styles.commentCancelBtn
                                                }
                                                size="small"
                                                variant="outlined"
                                              >
                                                Cancel
                                              </Button>
                                            </Stack>
                                          </Stack>
                                        ) : (
                                          // View sub-reply
                                          <Stack
                                            direction="row"
                                            spacing={1}
                                            alignItems="flex-start"
                                          >
                                            <Avatar
                                              alt={sub.assignedTo?.name}
                                              src={sub.assignedTo?.file}
                                              style={{
                                                backgroundColor: "#C7EEEF",
                                                color: "#055253",
                                                height: "32px",
                                                width: "32px",
                                                fontSize: "14px",
                                              }}
                                            >
                                              {sub.assignedTo?.name
                                                ?.charAt(0)
                                                .toUpperCase()}
                                            </Avatar>
                                            <Stack
                                              spacing={0.5}
                                              sx={{ flexGrow: 1 }}
                                            >
                                              <Stack
                                                direction="row"
                                                spacing={1}
                                                alignItems="center"
                                              >
                                                <Typography
                                                  sx={{
                                                    fontSize: "14px",
                                                    fontWeight: "bold",
                                                  }}
                                                >
                                                  {sub.assignedTo?.name ||
                                                    "Unknown User"}
                                                </Typography>
                                                <TimeAgo date={sub.date} />
                                              </Stack>
                                              <Typography sx={{paddingLeft:"10px"}}
                                                dangerouslySetInnerHTML={{
                                                  __html: sub.commentText,
                                                }}
                                              />
                                              {/* Add buttons (Edit, Delete, etc.) if needed */}
                                            </Stack>
                                          </Stack>
                                        )}
                                      </div>
                                    );
                                  })}
                                </Stack>
                              )}
                            </div>
                          );
                        })}
                      </Stack>
                    )}
                  </Stack>
                </Stack>
              )}
            </div>
          );
        })}
    </div>
  );
};

export default CommentSection;
